import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X, User, LogOut, FolderKanban, LayoutDashboard, Users } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import LanguageSelector from "./LanguageSelector";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_formula-factory/artifacts/pa7xi9nc_WhatsApp%20Image%202026-02-25%20at%2005.34.59.jpeg";

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();
  const { user, isAuthenticated, isAdmin, logout } = useAuth();

  const navLinks = [
    { name: t('nav.home'), path: "/" },
    { name: t('nav.configurator'), path: "/configurador" },
    { name: "Fábrica", path: "/fabrica" },
    { name: t('nav.about'), path: "/nosotros" },
    { name: t('nav.services'), path: "/servicios" },
    { name: t('nav.contact'), path: "/contacto" },
  ];

  const handleLogout = async () => {
    await logout();
    setUserMenuOpen(false);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group flex-shrink-0" data-testid="logo-link">
            <img 
              src={LOGO_URL} 
              alt="Unique Drinks Lab" 
              className="h-12 md:h-14 w-auto transition-transform duration-300 group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-4 xl:gap-6">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-${link.name.toLowerCase()}`}
                className={`text-xs xl:text-sm uppercase tracking-wider font-medium transition-colors duration-300 ${
                  location.pathname === link.path
                    ? "text-udl-primary"
                    : "text-udl-gray-400 hover:text-udl-white"
                }`}
              >
                {link.name}
              </Link>
            ))}
            
            {/* Language Selector */}
            <LanguageSelector className="border-l border-slate-700 pl-4 ml-2" />

            {/* Auth Section */}
            {isAuthenticated ? (
              <div className="relative ml-2">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800/50 border border-slate-700 hover:border-amber-500/50 transition-all"
                  data-testid="user-menu-btn"
                >
                  {user?.picture ? (
                    <img src={user.picture} alt="" className="w-6 h-6 rounded-full" />
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-amber-500/20 flex items-center justify-center">
                      <User className="w-4 h-4 text-amber-500" />
                    </div>
                  )}
                  <span className="text-sm text-white max-w-[100px] truncate">
                    {user?.name?.split(' ')[0]}
                  </span>
                </button>

                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-slate-800 border border-slate-700 rounded-lg shadow-xl py-1 animate-fade-in-up">
                    {isAdmin && (
                      <>
                        <Link
                          to="/admin"
                          onClick={() => setUserMenuOpen(false)}
                          className="flex items-center gap-2 px-4 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-white"
                        >
                          <FolderKanban className="w-4 h-4" />
                          {t('nav.admin')}
                        </Link>
                        <Link
                          to="/dashboard"
                          onClick={() => setUserMenuOpen(false)}
                          className="flex items-center gap-2 px-4 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-white"
                        >
                          <LayoutDashboard className="w-4 h-4" />
                          {t('nav.dashboard')}
                        </Link>
                        <Link
                          to="/crm"
                          onClick={() => setUserMenuOpen(false)}
                          className="flex items-center gap-2 px-4 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-white"
                        >
                          <Users className="w-4 h-4" />
                          {t('nav.crm')}
                        </Link>
                        <div className="border-t border-slate-700 my-1" />
                      </>
                    )}
                    <Link
                      to="/mis-proyectos"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-white"
                    >
                      <FolderKanban className="w-4 h-4" />
                      {t('nav.myProjects')}
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-2 px-4 py-2 text-sm text-red-400 hover:bg-slate-700 hover:text-red-300 w-full"
                    >
                      <LogOut className="w-4 h-4" />
                      {t('nav.logout')}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/auth"
                className="ml-2 px-4 py-2 text-sm text-white bg-amber-600 hover:bg-amber-700 rounded-lg transition-colors"
                data-testid="login-btn"
              >
                {t('nav.login')}
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden text-udl-white p-2"
            data-testid="mobile-menu-btn"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <div className="lg:hidden pb-6 animate-fade-in-up" data-testid="mobile-menu">
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className={`text-sm uppercase tracking-wider font-medium py-3 border-l-2 pl-4 transition-colors duration-300 ${
                    location.pathname === link.path
                      ? "text-udl-primary border-udl-primary"
                      : "text-udl-gray-400 border-udl-gray-800 hover:text-udl-white"
                  }`}
                >
                  {link.name}
                </Link>
              ))}
              
              {/* Mobile Auth */}
              {isAuthenticated ? (
                <>
                  <Link
                    to="/mis-proyectos"
                    onClick={() => setIsOpen(false)}
                    className="text-sm uppercase tracking-wider font-medium py-3 border-l-2 pl-4 text-udl-gray-400 border-udl-gray-800 hover:text-udl-white"
                  >
                    {t('nav.myProjects')}
                  </Link>
                  {isAdmin && (
                    <>
                      <Link
                        to="/admin"
                        onClick={() => setIsOpen(false)}
                        className="text-sm uppercase tracking-wider font-medium py-3 border-l-2 pl-4 text-udl-gray-400 border-udl-gray-800 hover:text-udl-white"
                      >
                        {t('nav.admin')}
                      </Link>
                      <Link
                        to="/dashboard"
                        onClick={() => setIsOpen(false)}
                        className="text-sm uppercase tracking-wider font-medium py-3 border-l-2 pl-4 text-udl-gray-400 border-udl-gray-800 hover:text-udl-white"
                      >
                        Dashboard
                      </Link>
                      <Link
                        to="/crm"
                        onClick={() => setIsOpen(false)}
                        className="text-sm uppercase tracking-wider font-medium py-3 border-l-2 pl-4 text-udl-gray-400 border-udl-gray-800 hover:text-udl-white"
                      >
                        CRM
                      </Link>
                    </>
                  )}
                  <button
                    onClick={() => { handleLogout(); setIsOpen(false); }}
                    className="text-sm uppercase tracking-wider font-medium py-3 border-l-2 pl-4 text-red-400 border-udl-gray-800 hover:text-red-300 text-left"
                  >
                    {t('nav.logout')}
                  </button>
                </>
              ) : (
                <Link
                  to="/auth"
                  onClick={() => setIsOpen(false)}
                  className="btn-primary text-center mt-4"
                >
                  {t('nav.login')}
                </Link>
              )}
              
              {/* Mobile Language */}
              <div className="pt-4 border-t border-slate-700 mt-2">
                <LanguageSelector />
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}
